import React, { useState, CSSProperties } from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import { SystemModalContainer } from '@/components/common/SystemModal/SystemModal.tsx';

type ProtectionChatModalProps = {
  handleClose: () => void;
};

const slides = [
  {
    image: '/help_center_1.png',
    title: 'profile.my_space.safety_recommendations.modal.tip1.title',
    description: 'profile.my_space.safety_recommendations.modal.tip1.subtitle'
  },
  {
    image: '/help_center_2.png',
    title: 'profile.my_space.safety_recommendations.modal.tip2.title',
    description: 'profile.my_space.safety_recommendations.modal.tip2.subtitle'
  },
  {
    image: '/help_center_3.png',
    title: 'profile.my_space.safety_recommendations.modal.tip3.title',
    description: 'profile.my_space.safety_recommendations.modal.tip3.subtitle'
  }
];

const HelpCenterRecommendationsModal: React.FC<ProtectionChatModalProps> = ({
  handleClose
}) => {
  const { t } = useTranslation();

  const [currentSlide, setCurrentSlide] = useState(0);

  const handleNext = () => {
    if (currentSlide < slides.length - 1) {
      setCurrentSlide((prev) => prev + 1);
    } else {
      handleClose();
    }
  };

  const { image, title, description } = slides[currentSlide];
  const buttonText =
    currentSlide === slides.length - 1
      ? t('earn.unlock_bonuses_popup.got_it')
      : t('button.continue');

  return (
    <SystemModalContainer headerTitle="" handleClose={handleClose}>
      <div style={containerStyle}>
        <motion.div
          key={currentSlide}
          initial={{ opacity: 0, x: 60 }}
          animate={{ opacity: 1, x: 0 }}
          exit={{ opacity: 0, x: -60 }}
          transition={{ duration: 0.35, ease: 'easeInOut' }}
          style={slideStyle}
        >
          <img src={image} alt={title} style={imageStyle} />
          <h2 style={titleStyle}>{t(title)}</h2>
          <p style={descriptionStyle}>{t(description)}</p>
        </motion.div>

        <div style={dotsContainerStyle}>
          {slides.map((_, index) => (
            <div
              key={index}
              style={{
                ...dotStyle,
                opacity: currentSlide === index ? 1 : 0.3
              }}
            />
          ))}
        </div>

        <div style={buttonsContainerStyle}>
          <button style={buttonStyle} onClick={handleNext}>
            {buttonText}
          </button>
          <button style={closeButtonStyle} onClick={handleClose}>
            {t('button.close')}
          </button>
        </div>
      </div>
    </SystemModalContainer>
  );
};

const containerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  textAlign: 'center',
  width: '100%'
};

const slideStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center'
};

const imageStyle: CSSProperties = {
  width: '100%',
  maxWidth: 280,
  borderRadius: 10,
  marginBottom: 10
};

const titleStyle: CSSProperties = {
  fontSize: 22,
  fontWeight: 700,
  marginBottom: 6
};

const descriptionStyle: CSSProperties = {
  fontSize: 16,
  fontWeight: 400,
  color: '#555',
  lineHeight: 1.4,
  maxWidth: '90%',
  marginBottom: 20,
  height: '100px'
};

const dotsContainerStyle: CSSProperties = {
  display: 'flex',
  justifyContent: 'center',
  gap: 6,
  marginBottom: 20
};

const dotStyle: CSSProperties = {
  width: 6,
  height: 6,
  borderRadius: '50%',
  backgroundColor: '#000',
  transition: 'opacity 0.3s ease'
};

const buttonsContainerStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 10,
  width: '100%'
};

const buttonStyle: CSSProperties = {
  width: '100%',
  maxWidth: 300,
  height: 50,
  padding: '12px 0',
  borderRadius: 30,
  backgroundColor: '#000',
  color: '#fff',
  fontSize: 16,
  fontWeight: 500,
  border: 'none',
  cursor: 'pointer'
};

const closeButtonStyle: CSSProperties = {
  width: '100%',
  maxWidth: 300,
  height: 50,
  padding: '12px 0',
  borderRadius: 30,
  backgroundColor: '#fff',
  color: '#000',
  fontSize: 16,
  fontWeight: 500,
  border: '1px solid #000',
  cursor: 'pointer'
};

export default HelpCenterRecommendationsModal;
